import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Modal,
  ModalClose,
  Typography,
  Sheet,
  Box,
} from "@mui/joy";
import AuthContext from "../../contexts/authContext";
import axiosInstance from "../../api/axios";
import toast from "react-hot-toast";

export default function Payments({
  openPaymentModal,
  setOpenPaymentModal,
  customerId,
  setCustomerId,
}) {
  const [amount, setAmount] = useState(0);
  const [paymentType, setPaymentType] = useState("cash");
  const { userData, setRefresh } = useContext(AuthContext);
  // const [remarks, setRemarks] = useState("");
  // const [date, setDate] = useState();
  const [custScheme, setCustScheme] = useState("");
  const [custData, setCustData] = useState([]);
  const [custSchemeId, setCustSchemeId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch customer data when modal opens
  useEffect(() => {
    if (customerId) {
      const customer = userData?.custSchemes?.filter(
        (ite) => ite.customerId?._id === customerId
      );
      setCustData(customer);
    }
  }, [userData, customerId]);

  useEffect(() => {
    setAmount(0);
    setPaymentType("cash");
    setCustScheme("");
    setCustSchemeId("");
    setIsSubmitting(false);
  }, [openPaymentModal]);
  // Handle scheme selection and set amount dynamically
  const handleSelectScheme = (newValue) => {
    setCustScheme(newValue);
    const selectedScheme = custData.find((ite) => ite._id === newValue);
    if (selectedScheme) {
      // setCustSchemeId(selectedScheme._id);
      setAmount(selectedScheme?.amount);
      setCustSchemeId(selectedScheme._id);
    }

    // const schemeAmount = custData.find((ite) => ite._id === newValue);
    // setAmount(schemeAmount.amount);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return; // prevent double submit
    setIsSubmitting(true);

    const numericAmount = Number(amount);
    let remarks = `Reached - ${numericAmount === 0 ? "NA" : `Collected`}`;
    const payType = numericAmount === 0 ? "NA" : paymentType;
    const agentId = localStorage.getItem("userId");

    try {
      const res = await axiosInstance.post(`/payments/add/${custSchemeId}`, {
        amountPaid: numericAmount,
        paymentType: payType,
        agentId: agentId,
        remarks: remarks,
        collectionDate: new Date().toLocaleDateString(),
        lat: "0",
        long: "0",
        customerId,
      });

      if (res.status === 200) {
        setRefresh(true);
        setOpenPaymentModal(false);
        setAmount(0);
        setCustomerId("");
        setPaymentType("cash");
        setCustSchemeId("");
        setIsSubmitting(false);
        setCustData([]);
        toast.success("Payment added successfully!");
      }
    } catch (error) {
      console.error("Error adding Payments", error);
      toast.error("Failed to add payment.");
      setIsSubmitting(false); // enable again only on error
    }
  };

  const handleClosePayments = () => {
    setOpenPaymentModal(false);
    setCustScheme("");
    setAmount(0);
    setPaymentType("cash");
    setCustomerId("");
  };

  return (
    <React.Fragment>
      <Modal
        aria-labelledby="modal-title"
        aria-describedby="modal-desc"
        open={openPaymentModal}
        onClose={handleClosePayments}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Sheet
          variant="outlined"
          sx={{
            maxWidth: 300,
            width: "100%",
            bgcolor: "#FEDDDD",
            p: 2,
            borderRadius: "md",
            boxShadow: "lg",
            m: 2,
          }}
        >
          <ModalClose
            variant="plain"
            sx={{
              m: 1,
              "& svg": { color: "#800001" },
              "&:hover": {
                backgroundColor: "#800001",
                "& svg": { color: "#fff" },
              },
            }}
          />
          <Box sx={{ mb: 1 }}>
            <Typography
              component="h2"
              id="modal-title"
              level="h4"
              textColor="inherit"
              sx={{ fontWeight: "lg" }}
            >
              {custData[0]?.customerId?.name}
            </Typography>
            <Typography>{custData[0]?.customerId?.mobile}</Typography>
          </Box>

          <Box>
            <form onSubmit={handleSubmit}>
              {/* Scheme Selector */}
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Scheme Name</FormLabel>
                <Select
                  value={custScheme}
                  onChange={(e, newValue) => handleSelectScheme(newValue)}
                  name="scheme"
                  required
                  sx={{ maxWidth: "100%" }}
                  slotProps={{
                    listbox: {
                      sx: { width: "100%" },
                    },
                  }}
                >
                  {custData?.map(
                    (ite) =>
                      ite?.status === "active" && (
                        <Option value={ite._id} key={ite._id}>
                          {ite.schemeId?.schemeName}
                        </Option>
                      )
                  )}
                </Select>
              </FormControl>

              {/* Amount Field - Only shown when scheme is selected */}
              {custScheme && (
                <FormControl sx={{ mb: 2 }}>
                  <FormLabel>Amount</FormLabel>
                  <Input
                    name="amount"
                    value={amount}
                    type="number"
                    onChange={(e) => setAmount(e.target.value)}
                    required
                  />
                </FormControl>
              )}

              {custScheme && amount > 0 && (
                <FormControl sx={{ mb: 2 }}>
                  <FormLabel>Payment Type</FormLabel>
                  <Select
                    value={paymentType}
                    onChange={(e, newValue) => setPaymentType(newValue)}
                    name="paymentType"
                    required
                    sx={{ maxWidth: "100%" }}
                    slotProps={{
                      listbox: {
                        sx: { width: "100%" },
                      },
                    }}
                  >
                    <Option value="cash">Cash</Option>
                    <Option value="card">Card</Option>
                    <Option value="upi">UPI</Option>
                  </Select>
                </FormControl>
              )}

              {/* Submit */}
              <Button
                type="submit"
                fullWidth
                disabled={isSubmitting}
                variant="solid"
                sx={{
                  mt: 3,
                  backgroundColor: isSubmitting ? "#b5b5b5" : "#800001",
                  color: "white",
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  "&:hover": {
                    backgroundColor: isSubmitting ? "#b5b5b5" : "#800001",
                    color: "#fff",
                  },
                }}
              >
                {isSubmitting ? "Processing..." : "Submit"}
              </Button>
            </form>
          </Box>
        </Sheet>
      </Modal>
    </React.Fragment>
  );
}
